import type { TFunction } from "i18next";
import { ChevronLeft, ChevronRight, Plug } from "lucide-react";
import { type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Badge } from "@/components/ui/v2/badge";
import { Button } from "@/components/ui/v2/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/v2/card";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/v2/carousel";
import EmptyState from "@/components/ui/v2/empty-state";
import { Skeleton } from "@/components/ui/v2/skeleton";
import { useCarousel } from "@/components/ui/v2/use-carousel";
import { ROUTES } from "@/constants/routes";
import { useIntegrations } from "@/features/integrations/hooks/useIntegrations";
import type { Integration } from "@/features/integrations/types";
import { encodeSlug } from "@/features/integrations/utils/slug";
import { translateIntegrationDisplayName } from "@/features/integrations/utils/translate-integration-display-name";
import { usePermissionMatrix } from "@/lib/hooks/usePermissionMatrix";
import { cn } from "@/lib/utils/cn";
import { getInitials } from "@/utils/stringUtils";

const MAX_MODULES_PER_CARD = 2;

export function IntegrationsSection({ clientId }: { clientId?: string }) {
  const { t, i18n } = useTranslation();
  const { data, isPending } = useIntegrations({ clientId, includes: ["modules"] });
  const i18nExists = (k: string) => i18n.exists(k);

  const integrations = data ?? [];
  const activeCount = integrations.filter((i) => i.isActive).length;
  const totalCount = integrations.length;

  const sortedActive = [...integrations]
    .filter((i) => i.isActive)
    .sort((a, b) => {
      const aTime = a.activatedDate ? new Date(a.activatedDate).getTime() : 0;
      const bTime = b.activatedDate ? new Date(b.activatedDate).getTime() : 0;
      return bTime - aTime;
    });

  const header = (
    <div className="flex flex-col gap-1">
      <h2 className="text-lg font-semibold text-foreground">
        {t("home.integrations.title")}
      </h2>
      <p className="text-sm text-muted-foreground">
        {t("home.integrations.subtitle", {
          active: activeCount,
          total: totalCount,
        })}
      </p>
    </div>
  );

  const activeBadge = !isPending && totalCount > 0 && (
    <Badge variant="success" size="medium">
      {t("home.integrations.activeBadge", { count: activeCount })}
    </Badge>
  );

  if (isPending) {
    return (
      <section className="flex flex-col gap-4">
        <header className="flex items-end justify-between gap-4">
          {header}
        </header>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-[220px] w-full" />
          ))}
        </div>
      </section>
    );
  }

  if (sortedActive.length === 0) {
    return (
      <section className="flex flex-col gap-4">
        <header className="flex items-end justify-between gap-4">
          {header}
          <div className="flex items-center gap-2">{activeBadge}</div>
        </header>
        <Card padding="md">
          <CardContent className="px-0">
            <EmptyState
              icon={Plug}
              title={t("home.integrations.empty.title")}
              description={t("home.integrations.empty.description")}
            />
          </CardContent>
        </Card>
      </section>
    );
  }

  return (
    <Carousel
      opts={{ align: "start", containScroll: "trimSnaps" }}
      className="w-full"
    >
      <section className="flex flex-col gap-4">
        <header className="flex items-end justify-between gap-4">
          {header}
          <div className="flex items-center gap-2">
            {activeBadge}
            <CarouselNav t={t} />
          </div>
        </header>
        <CarouselContent className="-ml-4">
          {sortedActive.map((integration) => (
            <CarouselItem
              key={integration.id}
              className="pl-4 basis-full sm:basis-1/2 md:basis-1/3 lg:basis-1/4"
            >
              <IntegrationCard
                integration={integration}
                clientId={clientId}
                t={t}
                i18nExists={i18nExists}
              />
            </CarouselItem>
          ))}
        </CarouselContent>
      </section>
    </Carousel>
  );
}

function CarouselNav({ t }: { t: TFunction }) {
  const { scrollPrev, scrollNext, canScrollPrev, canScrollNext } = useCarousel();
  return (
    <>
      <Button
        variant="outline"
        size="icon-sm"
        onClick={scrollPrev}
        aria-label={t("home.integrations.scrollPrev")}
        disabled={!canScrollPrev}
      >
        <ChevronLeft className="size-4" />
      </Button>
      <Button
        variant="outline"
        size="icon-sm"
        onClick={scrollNext}
        aria-label={t("home.integrations.scrollNext")}
        disabled={!canScrollNext}
      >
        <ChevronRight className="size-4" />
      </Button>
    </>
  );
}

interface IntegrationCardProps {
  integration: Integration;
  clientId?: string;
  t: TFunction;
  i18nExists: (key: string) => boolean;
}

function IntegrationCard({ integration, clientId, t, i18nExists }: IntegrationCardProps) {
  const navigate = useNavigate();
  const { canView } = usePermissionMatrix();
  const canOpen = canView("integrations");
  const i18nShim = { exists: i18nExists };
  const displayName =
    translateIntegrationDisplayName(integration.displayName, t, i18nShim) ||
    integration.name;
  const initials = getInitials(displayName);

  const activeModules = (integration.modules ?? [])
    .filter((m) => m.isActive)
    .slice(0, MAX_MODULES_PER_CARD);

  const handleOpen = () => {
    const base = clientId
      ? `/${ROUTES.clients}/${clientId}/${ROUTES.integrations}`
      : `/${ROUTES.integrations}`;
    navigate(`${base}/${encodeSlug(integration.name)}`);
  };

  const interactiveProps = canOpen
    ? {
      hover: true as const,
      onClick: handleOpen,
      role: "link",
      tabIndex: 0,
      onKeyDown: (e: KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleOpen();
        }
      },
      "aria-label": t("home.integrations.openIntegration", {
        name: displayName,
      }),
    }
    : {};

  return (
    <Card
      padding="md"
      {...interactiveProps}
      className={cn(
        "flex h-full flex-col gap-4 py-5",
        canOpen &&
          "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
      )}
    >
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 px-5">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-semibold text-foreground">
            {initials}
          </div>
          <CardTitle className="truncate text-base">{displayName}</CardTitle>
        </div>
        <Badge variant="success" size="small">
          {t("home.integrations.active")}
        </Badge>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 px-5 text-sm">
        {activeModules.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t("home.integrations.noActiveModules")}
          </p>
        ) : (
          activeModules.map((m) => (
            <div key={m.moduleName} className="flex gap-3">
              <span
                className="mt-1.5 size-2 shrink-0 rounded-full bg-emerald-500"
                aria-hidden
              />
              <span className="font-semibold text-foreground">
                {t(`integrations.modules.names.${m.moduleName}`, m.moduleName)}
              </span>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}
